import { Button, Field, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { hrApi, type PositionRow } from "../../api.js";
import { askReason, ErrorLine, useAction } from "../common.js";

export function PositionsTab() {
  const [selectedPosition, setSelectedPosition] = useState<PositionRow | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [checklistText, setChecklistText] = useState("");

  const positionsQuery = useQuery({
    queryKey: ["hr", "positions"],
    queryFn: () => hrApi.listPositions(),
  });

  const positionDetailQuery = useQuery({
    queryKey: ["hr", "positions", selectedPosition?.id, "description"],
    queryFn: () => (selectedPosition ? hrApi.getPositionDescription(selectedPosition.id) : null),
    enabled: !!selectedPosition,
  });

  const addPositionAction = useAction(async () => {
    const checklist = checklistText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    await hrApi.createPosition({
      name: name.trim(),
      description: description.trim() || undefined,
      checklist,
    });

    setShowAddModal(false);
    setName("");
    setDescription("");
    setChecklistText("");
  }, [["hr", "positions"]]);

  const archivePositionAction = useAction(
    async (pos: PositionRow) => {
      const reason = askReason(`سمت ${pos.name}`);
      if (!reason) return;
      await hrApi.archivePosition(pos.id, pos.version, reason);
      if (selectedPosition?.id === pos.id) {
        setSelectedPosition(null);
      }
    },
    [["hr", "positions"]],
  );

  const activeDetail = positionDetailQuery.data ?? selectedPosition;

  return (
    <div className="grid" style={{ gridTemplateColumns: activeDetail ? "1fr 1fr" : "1fr", gap: "var(--s4)" }}>
      {/* Positions List */}
      <Panel
        title="سمت‌ها و شرح وظایف سازمانی"
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setShowAddModal(true);
            }}
          >
            افزودن سمت جدید
          </Button>
        }
      >
        <div className="card" style={{ padding: "0", overflow: "hidden" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>نام سمت</th>
                <th>تعداد وظایف چک‌لیست</th>
                <th>خلاصه شرح وظایف</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(positionsQuery.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", padding: "var(--s4)" }} className="muted">
                    هنوز سمتی تعریف نشده است.
                  </td>
                </tr>
              ) : (
                (positionsQuery.data ?? []).map((pos) => {
                  const isSelected = pos.id === selectedPosition?.id;
                  return (
                    <tr
                      key={pos.id}
                      style={{
                        cursor: "pointer",
                        backgroundColor: isSelected ? "var(--bg-subtle, rgba(0,0,0,0.04))" : undefined,
                      }}
                      onClick={() => {
                        setSelectedPosition(pos);
                      }}
                    >
                      <td className="bold">{pos.name}</td>
                      <td>
                        <span className="num">{pos.checklist.length}</span> مورد
                      </td>
                      <td className="muted t-cap" style={{ maxWidth: "250px" }}>
                        {pos.description ? (
                          pos.description.length > 60 ? (
                            `${pos.description.slice(0, 60)}...`
                          ) : (
                            pos.description
                          )
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="end">
                        <Button
                          variant={isSelected ? "primary" : "ghost"}
                          size="md"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPosition(pos);
                          }}
                        >
                          مشاهده شرح وظایف
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* Position Detail View */}
      {activeDetail && (
        <Panel
          title={`شرح شغل: ${activeDetail.name}`}
          actions={
            <div className="row" style={{ gap: "var(--s2)" }}>
              <Button
                variant="ghost"
                onClick={() => {
                  setSelectedPosition(null);
                }}
              >
                بستن
              </Button>
              <Button
                variant="urgent"
                onClick={() => {
                  if (selectedPosition) archivePositionAction.run(selectedPosition);
                }}
                disabled={archivePositionAction.busy}
              >
                بایگانی سمت
              </Button>
            </div>
          }
        >
          <div className="stack" style={{ gap: "var(--s4)" }}>
            <div className="card" style={{ padding: "var(--s4)" }}>
              <div className="stack" style={{ gap: "var(--s3)" }}>
                <div>
                  <h3 className="t-title">{activeDetail.name}</h3>
                  <p className="muted" style={{ marginTop: "var(--s2)", lineHeight: "1.6" }}>
                    {activeDetail.description || "شرح شغلی برای این سمت ثبت نشده است."}
                  </p>
                </div>

                <div
                  style={{
                    borderTop: "1px solid var(--border)",
                    paddingTop: "var(--s3)",
                    marginTop: "var(--s2)",
                  }}
                >
                  <h4 className="bold" style={{ marginBottom: "var(--s2)" }}>
                    چک‌لیست وظایف ایستگاه / شیفت:
                  </h4>
                  {activeDetail.checklist.length > 0 ? (
                    <ul
                      style={{
                        paddingRight: "var(--s4)",
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      {activeDetail.checklist.map((item, idx) => (
                        <li key={idx} style={{ lineHeight: "1.5" }}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="muted t-cap">موردی برای چک‌لیست تعیین نشده است.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Panel>
      )}

      {/* Modal: Add Position */}
      {showAddModal && (
        <div className="modal-backdrop">
          <div className="card modal-card" style={{ maxWidth: "560px", width: "95%" }}>
            <div className="row wrap-between" style={{ alignItems: "center", marginBottom: "var(--s3)" }}>
              <h2 className="t-title">افزودن سمت سازمانی جدید</h2>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowAddModal(false);
                }}
              >
                بستن
              </Button>
            </div>

            <form
              className="stack"
              style={{ gap: "var(--s3)" }}
              onSubmit={(e) => {
                e.preventDefault();
                addPositionAction.run(undefined);
              }}
            >
              <Field
                id="position-name"
                label="نام سمت"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                required
                placeholder="مثال: سرآشپز برنج"
              />

              <div className="field">
                <label htmlFor="position-desc">شرح شغل و مسئولیت‌ها</label>
                <textarea
                  id="position-desc"
                  className="input"
                  style={{ minHeight: "90px", resize: "vertical" }}
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                  }}
                  placeholder="توضیح مسئولیت‌های روزانه، مهارت‌ها و اهداف این نقش..."
                />
              </div>

              <div className="field">
                <label htmlFor="position-checklist">چک‌لیست وظایف روزانه (هر خط یک مورد)</label>
                <textarea
                  id="position-checklist"
                  className="input"
                  style={{ minHeight: "100px", resize: "vertical" }}
                  value={checklistText}
                  onChange={(e) => {
                    setChecklistText(e.target.value);
                  }}
                  placeholder="مثال:&#10;کنترل دمای دیگ‌ها&#10;بررسی کیفیت برنج دم‌کشیده&#10;تطبیق زمان پخت با برنامه آماده‌سازی"
                />
              </div>

              <ErrorLine error={addPositionAction.error} />

              <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowAddModal(false);
                  }}
                  disabled={addPositionAction.busy}
                >
                  انصراف
                </Button>
                <Button variant="primary" type="submit" disabled={addPositionAction.busy}>
                  {addPositionAction.busy ? "در حال ذخیره..." : "ثبت سمت"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
